import React, { useEffect, useState, useCallback } from 'react';
import { Heart, Bookmark, ChevronRight, X, Loader2, Check, Plus } from 'lucide-react';
import { useGuestIdentity } from '../utils/useGuestIdentity';
import InspoSignInModal from './InspoSignInModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SaveToastHost — single global toast triggered by `window.dispatchEvent(new CustomEvent('crystal-save', {...}))`.
 *
 * Detail shape: { itemType, itemId, nowFav: boolean, label?: string }
 *
 * Behaviour:
 *  - nowFav=true  → "Saved to your inspo · Add to list" toast (with picker button)
 *  - nowFav=false → "Removed from your inspo" toast (auto-dismiss, no picker)
 *  - Tapping "Add to list" opens the bottom sheet (auth-gated via InspoSignInModal for guests).
 *
 * Mount once in App.js. Components only need to fire the event — no prop drilling.
 */
const SaveToastHost = () => {
  const [toast, setToast] = useState(null); // { itemType, itemId, nowFav, label? }
  const [showPicker, setShowPicker] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const { isRegistered, token } = useGuestIdentity();

  // Listen to global save events
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      setToast({ ...d, id: Date.now() });
    };
    window.addEventListener('crystal-save', handler);
    return () => window.removeEventListener('crystal-save', handler);
  }, []);

  // Auto-dismiss "removed" toasts after 2s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.nowFav ? 6000 : 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const openPicker = useCallback(() => {
    if (!isRegistered) {
      setShowSignIn(true);
      return;
    }
    setShowPicker(true);
  }, [isRegistered]);

  const closeAll = () => {
    setShowPicker(false);
    setToast(null);
  };

  return (
    <>
      {/* The toast — small floating pill, mobile-safe-area aware */}
      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[90] pointer-events-none"
          style={{ bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' }}
          data-testid="save-toast"
        >
          <div className="pointer-events-auto inline-flex items-center gap-3 bg-black text-white pl-4 pr-2 py-2 shadow-2xl border border-white/10"
               style={{ animation: 'toastIn 280ms cubic-bezier(0.22, 1, 0.36, 1) both' }}>
            <Heart size={14} className={toast.nowFav ? 'text-red-400' : 'text-white/40'} fill={toast.nowFav ? 'currentColor' : 'none'} />
            <span className="text-[11px] tracking-[0.15em] uppercase">
              {toast.nowFav ? 'Saved to your inspo' : 'Removed'}
            </span>
            {toast.nowFav && (
              <button
                type="button"
                onClick={openPicker}
                className="ml-1 inline-flex items-center gap-1 px-3 py-1.5 bg-white text-black text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-white/85 transition-colors"
                data-testid="save-toast-add-to-list"
              >
                <Bookmark size={11} /> Add to list <ChevronRight size={11} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setToast(null)}
              aria-label="Dismiss"
              className="ml-1 w-7 h-7 inline-flex items-center justify-center text-white/40 hover:text-white"
              data-testid="save-toast-dismiss"
            >
              <X size={14} />
            </button>
          </div>
          <style>{`@keyframes toastIn{0%{opacity:0;transform:translate(-50%,12px)}100%{opacity:1;transform:translate(-50%,0)}}`}</style>
        </div>
      )}

      {/* The bottom sheet — list picker */}
      {showPicker && toast && (
        <ListPickerSheet
          token={token}
          item={{ item_type: toast.itemType, item_id: toast.itemId }}
          onClose={() => setShowPicker(false)}
          onDone={() => closeAll()}
        />
      )}

      {/* Sign-in gate for unregistered guests */}
      <InspoSignInModal
        isOpen={showSignIn}
        onClose={() => setShowSignIn(false)}
        onSuccess={() => { setShowSignIn(false); setShowPicker(true); }}
        headline="Save to a list"
      />
    </>
  );
};

/* ─────────────── ListPickerSheet ─────────────── */
const ListPickerSheet = ({ token, item, onClose, onDone }) => {
  const [lists, setLists] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetch(`${API_URL}/api/inspo-lists/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (mounted) setLists(Array.isArray(d) ? d : []); })
      .catch(() => { if (mounted) setLists([]); });
    return () => { mounted = false; };
  }, [token]);

  const addToList = async (list) => {
    setBusy(true);
    setError('');
    try {
      const updatedRefs = (() => {
        const existing = list.item_refs || [];
        // De-dupe: if already in this list, no-op
        if (existing.some(r => r.item_type === item.item_type && r.item_id === item.item_id)) {
          return existing;
        }
        return [...existing, { item_type: item.item_type, item_id: item.item_id }];
      })();
      const res = await fetch(`${API_URL}/api/inspo-lists/${encodeURIComponent(list.slug)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ item_refs: updatedRefs }),
      });
      if (!res.ok) throw new Error('Could not add to list');
      onDone();
    } catch (err) {
      setError(err.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  const createList = async () => {
    setCreating(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/inspo-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          // server auto-generates name + slug when omitted
          item_refs: [{ item_type: item.item_type, item_id: item.item_id }],
        }),
      });
      if (!res.ok) throw new Error('Could not create list');
      onDone();
    } catch (err) {
      setError(err.message || 'Failed.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="list-picker-sheet"
    >
      <div className="w-full sm:max-w-md bg-white text-black border border-black/5 shadow-2xl"
           style={{ animation: 'sheetIn 320ms cubic-bezier(0.22, 1, 0.36, 1) both' }}>
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-black/5">
          <p className="text-[10px] tracking-[0.25em] uppercase text-black/40">Save to a list</p>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 inline-flex items-center justify-center text-black/40 hover:text-black tap-target" data-testid="list-picker-close">
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {lists === null ? (
            <div className="py-10 text-center"><Loader2 className="animate-spin inline" size={20} /></div>
          ) : lists.length === 0 ? (
            <div className="py-10 px-5 text-center">
              <Bookmark size={20} className="mx-auto mb-3 text-black/30" />
              <p className="text-sm text-black/55 mb-1">No lists yet.</p>
              <p className="text-xs text-black/40">Create your first list below to organize your inspiration.</p>
            </div>
          ) : (
            <ul>
              {lists.map(l => (
                <li key={l.slug}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => addToList(l)}
                    data-testid={`list-picker-item-${l.slug}`}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-black/[0.03] transition-colors disabled:opacity-50"
                  >
                    <div>
                      <p className="font-medium text-sm">{l.name || 'Untitled list'}</p>
                      <p className="text-[11px] text-black/40 mt-0.5">{(l.item_refs || []).length} saved</p>
                    </div>
                    <Check size={14} className="text-black/20" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-black/5 p-3">
          <button
            type="button"
            disabled={creating}
            onClick={createList}
            data-testid="list-picker-create-new"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black text-white text-xs tracking-[0.2em] uppercase font-medium hover:bg-black/85 transition-colors disabled:bg-black/40"
          >
            {creating ? <><Loader2 size={13} className="animate-spin" /> Creating…</> : <><Plus size={13} /> New list with this item</>}
          </button>
          {error && <p className="text-red-500 text-xs mt-2 text-center">{error}</p>}
        </div>
      </div>
      <style>{`@keyframes sheetIn{0%{opacity:0;transform:translateY(20px)}100%{opacity:1;transform:translateY(0)}}@media(min-width:640px){@keyframes sheetIn{0%{opacity:0;transform:translateY(10px) scale(0.98)}100%{opacity:1;transform:translateY(0) scale(1)}}}`}</style>
    </div>
  );
};

export default SaveToastHost;
